import { registrationConfig } from "@/helpers/config";
import { doPatch, doPost } from "@/helpers/fetch-helper";
import { useLoggedInStatus } from "@/helpers/loggedInStatus";
import { useMemberDetails } from "@/helpers/member-details";
import { studentCodes, useMembershipTypes } from "@/helpers/membership";
import { useShowSpinner } from "@/helpers/showSpinner";
import { useWorkplaceSearch } from "@/helpers/workplace-search";

function getSelectValues(item) {
  return Object.entries(item)
    .map(([value, text]) => ({
      value,
      text,
    }))
    .sort((a, b) => {
      const textA = a.text.toUpperCase();
      const textB = b.text.toUpperCase();
      if (textA < textB) {
        return -1;
      }
      if (textA > textB) {
        return 1;
      }

      return 0;
    });
}

export const AboutYouDefinition = {
  pages: [
    {
      id: 0,
      title: "",
      text: "",
      isMembershipWizard: false,
    },
    {
      id: 1,
      title: "My membership category",
      text:
        "Please select your required membership below. If you have never had a membership before, you may not know which membership is relevant for you. If that is the case, please click the button below and our membership wizard will help select the right option for you.",
      isMembershipWizard: true,
    },
  ],
};

export const ContactDetailsDefinition = {
  pages: [
    {
      id: 0,
      title: () => {
        return "Email Address";
      },
      text:
        `<div class="personal-email-box">Please enter a <strong>personal email address</strong>. This email address needs to be accessible at all times.</div>` +
        "<p><b>A verification email will be sent to active your account</b></p>",
      isMembershipWizard: false,
      isForm: true,
      skipIfLoggedIn: true,
      completionAction: async function(fields) {
        const data = fields.reduce((acc, cur) => {
          acc[cur.key] = cur.value;
          return acc;
        }, {});

        const { existing } = useMemberDetails();

        const response = await doPost("/checkaccountemail", {
          ...data,
          existing: existing.value,
        });

        let message;
        if (response.success) {
          const { setMemberUserNameData } = useMemberDetails();
          setMemberUserNameData(response.email);
        } else {
          switch (response.reason) {
            case "EEXISTS":
              message = `It appears you have already registered. <a href="/auth/requestsignin?returnurl=${window.location.pathname}">Login to continue</a>`;
              break;
            case "MEXISTS":
              message = `It appears you have a membership already. <a href="/register/create-account">Find your membership to continue</a>`;
              break;
            default:
              message = `We could not create your account. Please <a href="/about/society-of-radiographers/contact-us">contact us</a>`;
          }
        }

        return {
          success: response.success,
          message,
          persistent: !!message,
          linked: response.linked,
        };
      },
      fields: [
        {
          type: "email",
          key: "email",
          label: "Personal email address",
          placeholder: "your@email.com",
          value: "",
          rules: ["email", "required"],
          maxlength: 254,
        },
      ],
    },
    {
      id: 0,
      title: "Create Password",
      isForm: true,
      skipIfLoggedIn: true,
      completionAction: async function(fields) {
        const data = fields.reduce((acc, cur) => {
          acc[cur.key] = cur.value;
          return acc;
        }, {});

        const { typeCode, statusCode } = useMembershipTypes();
        const { existing, membershipNumber, userName } = useMemberDetails();
        const response = await doPost("/account", {
          ...data,
          memTypeCode: typeCode.value,
          memStatusCode: statusCode.value,
          existing: existing.value,
          membershipNumber: membershipNumber.value,
          email: userName.value,
        });

        let message;
        if (response.success) {
          message = "Successfully created your user account.";
          const { setIsLoggedIn } = useLoggedInStatus();
          setIsLoggedIn(true);
        } else {
          message = "We could not create your account";
        }

        return {
          success: response.success,
          message,
          persistent: !!message,
          linked: response.linked,
        };
      },
      fields: [
        {
          key: "password",
          type: "password",
          label: "Password",
          placeholder: "your password",
          validate: true,
          value: "",
          rules: ["required"],
        },
      ],
    },
    {
      id: 0,
      title: "Account Verified",
      text: "Thank you for verifing your account",
      isMembershipWizard: false,
      isForm: false,
      skipIfLoggedIn: true,
    },
    {
      id: 0,
      title: "About You",
      text: "",
      isMembershipWizard: false,
      usePostCodeLookup: false,
      isForm: true,
      isFormFlex: true,
      skipIfLoggedIn: false,
      async completionAction(fields) {
        const data = fields.reduce((acc, cur) => {
          if (cur.fields) {
            acc[cur.key] = cur.fields.reduce((x, y) => {
              x[y.key] = y.value;
              return x;
            }, {});
          } else {
            acc[cur.key] = cur.value;
          }

          return acc;
        }, {});

        const response = await doPatch("/personaldetails", data);

        if (response.success) {
          const details = useMemberDetails();

          for (const key of Object.keys(data)) {
            if (key === "homeAddress") {
              for (const homeAddressKey of Object.keys(data["homeAddress"])) {
                details[homeAddressKey].value =
                  data["homeAddress"][homeAddressKey];
              }
            } else {
              details[key].value = data[key];
            }
          }
        }

        return {
          success: response.success,
          message: response.success
            ? "Successfully adding your details"
            : response.errors.join(", "),
        };
      },
      fields: [
        {
          id: 1,
          type: "dropdown",
          key: "title",
          label: "Title",
          rules: ["required"],
          value: "",
          options: getSelectValues(registrationConfig.titles),
        },
        {
          id: 1,
          type: "text",
          key: "firstName",
          label: "First name",
          rules: ["required"],
          value: "",
        },
        {
          id: 3,
          type: "text",
          key: "lastName",
          label: "Last name",
          rules: ["required"],
          value: "",
        },
        {
          id: 6,
          type: "date",
          key: "dateOfBirth",
          label: "Date of birth",
          value: "",
          dateConfig: {
            maxDate: new Date().setFullYear(new Date().getFullYear() - 17),
            minDate: new Date().setFullYear(new Date().getFullYear() - 120),
          },
          rules: ["required"],
        },
        {
          id: 7,
          type: "address",
          key: "homeAddress",
          fields: [
            {
              id: 1,
              type: "text",
              key: "street",
              label: "Street",
              rules: ["required"],
              value: "",
            },
            {
              id: 2,
              type: "text",
              key: "town",
              label: "Town",
              rules: ["required"],
              value: "",
            },
            {
              id: 3,
              type: "text",
              key: "county",
              label: "County/Province",
              rules: ["required"],
              value: "",
            },
            {
              id: 4,
              type: "text",
              key: "postCode",
              label: "Post/ZIP code",
              rules: ["required"],
              value: "",
            },
            {
              id: 5,
              type: "dropdown",
              key: "country",
              label: "Country",
              rules: [
                "required",
                "text",
                "oneOf:" +
                  Object.keys(registrationConfig.countries).reduce(
                    (acc, c) => `${acc},${c}`,
                  ),
              ],
              value: "",
              options: getSelectValues(registrationConfig.countries),
            },
          ],
        },
        {
          type: "tel",
          key: "mobilePhone",
          label: "Mobile Phone number",
          value: "",
          rules: ["required", "phoneNumValidate", "min:11"],
          minlength: 11,
          hint:
            "Do not include spaces. For international numbers, please include the country code. E.g. +44",
        },
      ],
    },
  ],
};

export const ReviewDefinition = {
  pages: [
    {
      id: 0,
      title: "Review your details",
      text: "Review the details you've submitted so far",
      questions: [],
      fields: [
        {
          type: "whereFound",
          key: "whereFound",
          label: "Please let us know how you found out about SoR membership",
          options: getSelectValues(registrationConfig.memWhereFoundTypes),
          value: "",
          rules: ["required"],
        },
      ],
    },
  ],
};

export const WorkDetailsDefinition = {
  pages: [
    {
      title: "Place of Work",
      text: "",
      showWorkplaceSearch: true,
      fields: [
        {
          type: "email",
          key: "workEmail",
          label: "Work email address",
          rules: ["email"],
          value: "",
        },
        {
          type: "text",
          key: "hcpcNumber",
          label: "HCPC Number",
          rules: ["required", "hcpc"],
          value: "",
          hideIf() {
            const { typeCode, statusCode } = useMembershipTypes();
            return !(
              typeCode.value === "MEM" &&
              ["ORD", "NP1", "NP2"].includes(statusCode.value)
            );
          },
        },
      ],
      async completionAction(fields) {
        const { chosenWorkplace } = useWorkplaceSearch();
        const {
          membershipType,
          membershipStatusType,
          isAgencyWorker,
        } = useMemberDetails();

        const data = fields.reduce((acc, cur) => {
          acc[cur.key] = cur.value;
          return acc;
        }, {});

        const response = await doPatch("/workplacedetails", {
          ...data,
          workplaceId: chosenWorkplace.workplaceId,
          memTypeCode: membershipType.value,
          memStatusCode: membershipStatusType.value,
          isAgencyWorker: isAgencyWorker.value,
        });

        let message;
        if (response.success) {
          const details = useMemberDetails();
          message = "Successfully saved your employer details";
          for (const key of Object.keys(data)) {
            if (details[key]) details[key].value = data[key];
          }
        } else {
          switch (response.reason) {
            case "HCPC_IN_USE":
              message = `Your HCPC number is already registered. <a href="/auth/requestsignin?returnurl=${window.location.pathname}">Do you have an account with another email. If so login to that account to continue.</a>`;
              break;
            case "HCPC_EXISTS":
              message = `Your HCPC number already exists. <a href="/register/create-account">Find your membership to continue</a>`;
              break;
            default:
              message = "We could not create your account";
          }
        }

        return {
          success: response.success,
          message,
        };
      },
    },
  ],
};

export const EducationDetailsDefinition = {
  pages: [
    {
      title: "Place of Study",
      text: "",
      showWorkplaceSearch: false,
      questions: [],
      fields: [
        {
          id: 0,
          type: "university",
          key: "university",
          label: () => {
            const { statusCode } = useMembershipTypes();
            return !studentCodes.includes(statusCode.value)
              ? "Where did you study?"
              : "Where are you studing?";
          },
          value: "",
          options: getSelectValues(registrationConfig.universities),
        },
        {
          id: 1,
          type: "dropdown",
          key: "course",
          label: () => {
            const { statusCode } = useMembershipTypes();
            return !studentCodes.includes(statusCode.value)
              ? "What type of course did you study?"
              : "What type of course are you studing?";
          },
          rules: ["required"],
          options: getSelectValues(registrationConfig.courses),
          value: "",
        },
        {
          id: 2,
          type: "dropdown",
          key: "discipline",
          label: "Discipline",
          rules: ["required"],
          options: getSelectValues(registrationConfig.disciplines),
          value: "",
        },
        {
          id: 3,
          type: "dropdown",
          key: "yearOfStudy",
          label: "Year of Study",
          rules: ["required"],
          options: getSelectValues(registrationConfig.pointsOfEntry),
          value: "",
          hideIf() {
            const { statusCode } = useMembershipTypes();
            return !studentCodes.includes(statusCode.value);
          },
        },
        {
          id: 4,
          type: "dropdown",
          key: "lengthOfCourse",
          label: "Length of course",
          rules: ["required"],
          options: [1, 2, 3, 4].map((i) => ({ text: i, value: i })),
          value: "",
        },
        {
          id: 5,
          type: "date",
          key: "startDate",
          label: "Approximate Start Date",
          rules: ["required"],
          value: "",
          dateConfig: {
            minDate: new Date().setFullYear(new Date().getFullYear() - 6),
            maxDate: new Date().setFullYear(new Date().getFullYear() + 2),
          },
          hideIf() {
            const { statusCode } = useMembershipTypes();
            return !(
              studentCodes.includes(statusCode.value) ||
              statusCode.value == "APR"
            );
          },
        },
        {
          id: 6,
          type: "dropdown",
          key: "qualificationOnEntry",
          label: "Qualification on Entry",
          rules: ["required"],
          options: getSelectValues(registrationConfig.qualificationsOnEntry),
          value: "",
        },
        {
          id: 7,
          type: "date",
          key: "dateQualified",
          label: "When did you complete your qualification?",
          value: "",
          rules: ["required"],
          disallowFutureDate: true,
          orderYears: true,
          hideIf() {
            const { statusCode } = useMembershipTypes();
            return (
              studentCodes.includes(statusCode.value) ||
              statusCode.value === "APR"
            );
          },
        },
        {
          id: 8,
          type: "date",
          key: "expectedQualified",
          label: "When do you expect to complete your course?",
          value: "",
          rules: ["required"],
          dateConfig: {
            maxDate: new Date().setFullYear(new Date().getFullYear() + 6),
            minDate: new Date().setFullYear(new Date().getFullYear()),
          },
          orderYears: true,
          hideIf() {
            const { statusCode } = useMembershipTypes();
            return !(
              studentCodes.includes(statusCode.value) ||
              statusCode.value == "APR"
            );
          },
        },
      ],
      async completionAction(fields) {
        const { setShowSpinner } = useShowSpinner();
        setShowSpinner(true);
        const data = fields.reduce((acc, cur) => {
          acc[cur.key] = cur.value;
          return acc;
        }, {});

        const {
          university,
          universityOtherCountry,
          universityOtherName,
          course,
          discipline,
          yearOfStudy,
          lengthOfCourse,
          qualificationOnEntry,
          dateQualified,
          expectedQualified,
          membershipStatusType,
          isActivating,
        } = useMemberDetails();

        const resp = await doPatch("/education", {
          ...data,
          university: university.value,
          universityOtherCountry: universityOtherCountry.value,
          universityOtherName: universityOtherName.value,
          memberCyStatus: membershipStatusType.value,
        });

        if (resp.success) {
          dateQualified.value = data.dateQualified;
          course.value = data.course;
          discipline.value = data.discipline;
          yearOfStudy.value = data.yearOfStudy;
          lengthOfCourse.value = data.lengthOfCourse;
          qualificationOnEntry.value = data.qualificationOnEntry;
          dateQualified.value = data.dateQualified;
          expectedQualified.value = data.expectedQualified;
          membershipStatusType.value = resp.status;
          isActivating.value = resp.prePaid;
        }

        setShowSpinner(false);

        return resp;
      },
    },
  ],
};

export const CreateAccountDefinition = {
  pages: [
    {
      title: "Create your account",
      text:
        "To register for an account, we need some information to ensure you have a membership with us.",
      showWorkplaceSearch: true,
      questions: [],
      fields: [
        {
          type: "text",
          key: "lastName",
          label: "Last name",
          placeholder: "Your last name",
          rules: ["required"],
          value: "",
        },
        {
          type: "text",
          key: "membershipNumber",
          label: "SoR Membership number",
          placeholder: "# SoR membership number",
          rules: ["required", "alphanumeric"],
          value: "",
        },
        {
          type: "date",
          key: "dateOfBirth",
          label: "Date of birth",
          placeholder: "",
          rules: ["required"],
          value: "",
          dateConfig: {
            maxDate: new Date().setFullYear(new Date().getFullYear() - 17),
            minDate: new Date().setFullYear(new Date().getFullYear() - 120),
          },
        },
      ],
    },
  ],
};

export const DiversityDefinition = {
  pages: [
    {
      title: "Diversity Details",
      text: "",
      fields: [
        {
          type: "dropdown",
          key: "gender",
          label: "Gender",
          rules: ["required"],
          value: "",
          options: getSelectValues(registrationConfig.genders),
        },
        {
          type: "dropdown",
          key: "nationality",
          label: "Nationality",
          rules: ["required"],
          value: "",
          options: getSelectValues(registrationConfig.nationalities),
        },
        {
          type: "dropdown",
          key: "ethnicity",
          label: "Ethnicity",
          rules: ["required"],
          value: "",
          options: getSelectValues(registrationConfig.ethnicities),
        },
        {
          type: "checkbox",
          key: "hasDisability",
          label: "Do you consider yourself to have a disability?",
          value: "",
          options: [],
        },
      ],
      async completionAction(fields) {
        const { setShowSpinner } = useShowSpinner();
        setShowSpinner(true);
        const data = fields.reduce((acc, cur) => {
          acc[cur.key] = cur.value;
          return acc;
        }, {});

        const {
          gender,
          ethnicity,
          nationality,
          hasDisability,
        } = useMemberDetails();

        const resp = await doPatch("/diversity", data);

        if (resp.success) {
          gender.value = data.gender;
          ethnicity.value = data.ethnicity;
          nationality.value = data.nationality;
          hasDisability.value = data.hasDisability;
        }

        setShowSpinner(false);

        return resp;
      },
    },
  ],
};
